import React, { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import './PrivacyPolicy.css';

function PrivacyPolicy() {
  const navigate = useNavigate();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="privacy-policy-page">
      <div className="privacy-header">
        <div className="privacy-nav">
          <img 
            src="https://vahaan-buy-and-sell-category-images.s3.ap-south-1.amazonaws.com/VahaanBazar_Image.png" 
            alt="VahaanBazar" 
            className="privacy-logo"
            onClick={() => navigate('/')}
            style={{ cursor: 'pointer' }}
          />
          <button className="back-home-btn" onClick={() => navigate('/')}>
            ← Back to Home
          </button>
        </div>
        <h1>Privacy Policy</h1>
        <p className="last-updated">Last Updated: October 5, 2026</p>
      </div>

      <div className="privacy-content">
        <section className="privacy-section">
          <h2>1. Introduction</h2>
          <p>
            Welcome to Vahaan Bazar Private Limited ("Vahaan Bazar", "we", "us", or "our"). 
            We are committed to protecting your privacy and ensuring the security of your personal information. 
            This Privacy Policy explains how we collect, use, disclose, and safeguard your information when you 
            use our website, mobile application, and services.
          </p>
          <p>
            By accessing or using Vahaan Bazar's services, you agree to the collection and use of information 
            in accordance with this Privacy Policy. If you do not agree with our policies and practices, 
            please do not use our services.
          </p>
        </section>

        <section className="privacy-section">
          <h2>2. Information We Collect</h2>
          
          <h3>2.1 Personal Information</h3>
          <p>We may collect the following types of personal information:</p>
          <ul>
            <li><strong>Contact Information:</strong> Name, email address, phone number, mailing address</li>
            <li><strong>Identity Information:</strong> Government-issued ID, PAN card, Aadhaar details (as required by law)</li>
            <li><strong>Financial Information:</strong> Bank account details, payment information for transactions</li>
            <li><strong>Vehicle Information:</strong> Vehicle registration details, purchase/sale history, inspection reports</li>
            <li><strong>Business Information:</strong> For franchise partners - business license, GST number, business address</li>
          </ul>

          <h3>2.2 Automatically Collected Information</h3>
          <ul>
            <li><strong>Device Information:</strong> IP address, browser type, device type, operating system</li>
            <li><strong>Usage Data:</strong> Pages visited, time spent, click patterns, search queries</li>
            <li><strong>Location Data:</strong> GPS location (with your permission) for location-based services</li>
            <li><strong>Cookies and Tracking:</strong> We use cookies and similar technologies to enhance user experience</li>
          </ul>

          <h3>2.3 Information from Third Parties</h3>
          <ul>
            <li>Verification services for identity and vehicle documentation</li>
            <li>Payment processors for transaction information</li>
            <li>Insurance and finance partners for policy and loan applications</li>
            <li>Social media platforms if you choose to connect your account</li>
          </ul>
        </section>

        <section className="privacy-section">
          <h2>3. How We Use Your Information</h2>
          <p>We use the collected information for the following purposes:</p>
          
          <h3>3.1 Service Delivery</h3>
          <ul>
            <li>Facilitate vehicle buying, selling, and auction transactions</li>
            <li>Process payments and manage your account</li>
            <li>Provide inspection, valuation, and verification services</li>
            <li>Connect you with insurance and finance partners</li>
            <li>Manage franchise operations and support</li>
          </ul>

          <h3>3.2 Communication</h3>
          <ul>
            <li>Send transactional updates and service notifications</li>
            <li>Respond to your inquiries and support requests</li>
            <li>Provide marketing communications (with your consent)</li>
            <li>Send newsletters and promotional offers</li>
          </ul>

          <h3>3.3 Improvement and Analytics</h3>
          <ul>
            <li>Analyze user behavior to improve our services</li>
            <li>Conduct research and development for new features</li>
            <li>Monitor and analyze trends, usage, and activities</li>
            <li>Detect and prevent fraud and security incidents</li>
          </ul>

          <h3>3.4 Legal Compliance</h3>
          <ul>
            <li>Comply with applicable laws and regulations</li>
            <li>Respond to legal requests and prevent illegal activities</li>
            <li>Enforce our terms and conditions</li>
            <li>Protect our rights, privacy, safety, or property</li>
          </ul>
        </section>

        <section className="privacy-section">
          <h2>4. Information Sharing and Disclosure</h2>
          <p>We may share your information in the following circumstances:</p>

          <h3>4.1 With Your Consent</h3>
          <p>We share information when you explicitly consent to such sharing.</p>

          <h3>4.2 Service Providers</h3>
          <ul>
            <li>Payment processors and financial institutions</li>
            <li>Vehicle inspection and valuation partners</li>
            <li>Insurance and finance companies</li>
            <li>Technology and infrastructure service providers</li>
            <li>Marketing and analytics partners</li>
          </ul>

          <h3>4.3 Business Transfers</h3>
          <p>
            In case of merger, acquisition, or sale of assets, your information may be transferred 
            to the acquiring entity, subject to this Privacy Policy.
          </p>

          <h3>4.4 Legal Requirements</h3>
          <p>We may disclose information when required by law, court order, or government request.</p>

          <h3>4.5 Buyers and Sellers</h3>
          <p>
            For transaction purposes, we share relevant information between buyers and sellers, 
            including contact details and vehicle information.
          </p>
        </section>

        <section className="privacy-section">
          <h2>5. Data Security</h2>
          <p>We implement appropriate technical and organizational measures to protect your information:</p>
          <ul>
            <li>Encryption of sensitive data in transit and at rest</li>
            <li>Secure servers with restricted access controls</li>
            <li>Regular security audits and vulnerability assessments</li>
            <li>Employee training on data protection and confidentiality</li>
            <li>Multi-factor authentication for account access</li>
          </ul>
          <p className="security-note">
            <strong>Note:</strong> While we strive to protect your information, no method of transmission 
            over the internet is 100% secure. We cannot guarantee absolute security.
          </p>
        </section>

        <section className="privacy-section">
          <h2>6. Your Rights and Choices</h2>
          
          <h3>6.1 Access and Correction</h3>
          <p>You have the right to access and update your personal information through your account settings.</p>

          <h3>6.2 Data Deletion</h3>
          <p>
            You can request deletion of your account and personal data by contacting us at 
            <a href="mailto:privacy@vahaanbazar.in"> privacy@vahaanbazar.in</a>. 
            Some information may be retained as required by law.
          </p>

          <h3>6.3 Marketing Opt-Out</h3>
          <p>
            You can opt-out of marketing communications by clicking "unsubscribe" in our emails 
            or adjusting your account preferences.
          </p>

          <h3>6.4 Cookie Management</h3>
          <p>You can control cookies through your browser settings. Note that disabling cookies may affect functionality.</p>

          <h3>6.5 Location Services</h3>
          <p>You can disable location tracking through your device settings at any time.</p>
        </section>

        <section className="privacy-section">
          <h2>7. Data Retention</h2>
          <p>We retain your personal information for as long as necessary to:</p>
          <ul>
            <li>Provide our services and maintain your account</li>
            <li>Comply with legal obligations (typically 7 years for financial records)</li>
            <li>Resolve disputes and enforce agreements</li>
            <li>Support business operations and analytics</li>
          </ul>
          <p>
            After the retention period, we will securely delete or anonymize your information 
            unless longer retention is required by law.
          </p>
        </section>

        <section className="privacy-section">
          <h2>8. Children's Privacy</h2>
          <p>
            Vahaan Bazar's services are not intended for individuals under the age of 18. 
            We do not knowingly collect personal information from children. If you believe we have 
            collected information from a child, please contact us immediately, and we will take 
            steps to delete such information.
          </p>
        </section>

        <section className="privacy-section">
          <h2>9. Third-Party Links</h2>
          <p>
            Our services may contain links to third-party websites, applications, or services. 
            We are not responsible for the privacy practices of these third parties. We encourage 
            you to review their privacy policies before providing any information.
          </p>
        </section>

        <section className="privacy-section">
          <h2>10. International Data Transfers</h2>
          <p>
            Your information may be transferred to and processed in countries other than your own. 
            We ensure appropriate safeguards are in place to protect your information in compliance 
            with applicable data protection laws.
          </p>
        </section>

        <section className="privacy-section">
          <h2>11. Changes to This Privacy Policy</h2>
          <p>
            We may update this Privacy Policy from time to time to reflect changes in our practices 
            or legal requirements. We will notify you of significant changes by:
          </p>
          <ul>
            <li>Posting the updated policy on our website and app</li>
            <li>Updating the "Last Updated" date at the top of this page</li>
            <li>Sending email notifications for material changes</li>
          </ul>
          <p>
            Your continued use of our services after changes are posted constitutes acceptance 
            of the updated Privacy Policy.
          </p>
        </section>

        <section className="privacy-section">
          <h2>12. Contact Us</h2>
          <p>If you have questions, concerns, or requests regarding this Privacy Policy or our data practices, please contact us:</p>
          
          <div className="contact-info">
            <div className="contact-item">
              <strong>Vahaan Bazar Private Limited</strong>
            </div>
            <div className="contact-item">
              <strong>Email:</strong> <a href="mailto:privacy@vahaanbazar.in">privacy@vahaanbazar.in</a>
            </div>
            <div className="contact-item">
              <strong>Support:</strong> <a href="mailto:support@vahaanbazar.in">support@vahaanbazar.in</a>
            </div>
            <div className="contact-item">
              <strong>Phone:</strong> <a href="tel:+919876543210">+91 98765 43210</a>
            </div>
            <div className="contact-item">
              <strong>Address:</strong><br />
              Plot no 20A, Block no 43, Shutter 3,<br />
              beside Panduranga Agencies,<br />
              Autonagar, Hyderabad - 500070<br />
              Telangana, India
            </div>
          </div>
        </section>

        <section className="privacy-section">
          <h2>13. Grievance Officer</h2>
          <p>
            In accordance with Information Technology Act 2000 and rules made thereunder, 
            the name and contact details of the Grievance Officer are provided below:
          </p>
          <div className="contact-info">
            <div className="contact-item">
              <strong>Name:</strong> Grievance Officer - Vahaan Bazar
            </div>
            <div className="contact-item">
              <strong>Email:</strong> <a href="mailto:grievance@vahaanbazar.in">contact@vahaanbazar.in</a>
            </div>
            <div className="contact-item">
              <strong>Response Time:</strong> Within 48 hours of receiving your complaint
            </div>
          </div>
        </section>

        <div className="privacy-footer-section">
          <p className="acknowledgment">
            By using Vahaan Bazar's services, you acknowledge that you have read, understood, 
            and agree to be bound by this Privacy Policy.
          </p>
          <button className="back-to-top" onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}>
            ↑ Back to Top
          </button>
        </div>
      </div>

      <footer className="privacy-footer">
        <div className="footer-content">
          <div className="footer-logo">
            <img 
              src="https://vahaan-buy-and-sell-category-images.s3.ap-south-1.amazonaws.com/VahaanBazar_Image.png" 
              alt="VahaanBazar"
              onClick={() => navigate('/')}
              style={{ cursor: 'pointer' }}
            />
          </div>
          <div className="footer-links">
            <a href="/">Home</a>
            <a href="/terms-and-conditions">Terms & Conditions</a>
            <a href="/privacy-policy">Privacy Policy</a>
            <a href="/franchise">Franchise</a>
            <a href="/careers">Careers</a>
          </div>
          <p>&copy; 2026 Vahaan Bazar Private Limited. All Rights Reserved.</p>
        </div>
      </footer>
    </div>
  );
}

export default PrivacyPolicy;
